// components/SearchableSelect.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ChevronDown, X, CheckCircle } from 'lucide-react';

export default function SearchableSelect({ 
  options = [], 
  value, 
  onChange, 
  placeholder = 'Seleccionar...',
  disabled = false,
  error = false,
  labelKey = 'label',
  valueKey = 'value',
  maxHeight = 200
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const dropdownRef = useRef(null);

  // Filtrar opciones según búsqueda
  const filteredOptions = options.filter(option => {
    const label = typeof option === 'object' ? option[labelKey] : option;
    return label?.toString().toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Obtener el label del valor seleccionado
  const getSelectedLabel = useCallback(() => {
    if (value === null || value === undefined || value === '') return '';
    const selected = options.find(option => {
      const optionValue = typeof option === 'object' ? option[valueKey] : option;
      return optionValue === value;
    });
    return selected ? (typeof selected === 'object' ? selected[labelKey] : selected) : '';
  }, [value, options, valueKey, labelKey]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Actualizar label cuando cambia el valor
  useEffect(() => {
    setSelectedLabel(getSelectedLabel());
  }, [getSelectedLabel]);

  const handleSelect = (option) => {
    const optionValue = typeof option === 'object' ? option[valueKey] : option;
    const optionLabel = typeof option === 'object' ? option[labelKey] : option;
    
    onChange(optionValue);
    setSelectedLabel(optionLabel);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSelectedLabel('');
    setSearchTerm('');
  };

  return (
    <div className="searchable-select" ref={dropdownRef}>
      <div 
        className={`searchable-select-trigger ${isOpen ? 'open' : ''} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="searchable-select-value">
          {selectedLabel || <span className="placeholder">{placeholder}</span>}
        </div>
        <div className="searchable-select-actions">
          {selectedLabel && !disabled && (
            <button 
              className="searchable-select-clear"
              onClick={handleClear}
              type="button"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown size={16} className={`searchable-select-arrow ${isOpen ? 'rotate' : ''}`} />
        </div>
      </div>
      
      {isOpen && !disabled && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div className="searchable-select-options" style={{ maxHeight: `${maxHeight}px` }}>
            {filteredOptions.length === 0 ? (
              <div className="searchable-select-empty">No hay resultados</div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = typeof option === 'object' ? option[valueKey] : option;
                const optionLabel = typeof option === 'object' ? option[labelKey] : option;
                const isSelected = optionValue === value;
                
                return (
                  <div
                    key={optionValue || index}
                    className={`searchable-select-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(option)}
                  >
                    <span>{optionLabel}</span>
                    {isSelected && <CheckCircle size={14} />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}